/**
 * Three server answers into one render tree (`KLQ-20260819-005`).
 *
 * Pure, and separate from the components for the reason `tabs.ts` is: the join is the part
 * with rules in it, and rules that live inside a `.tsx` are rules nobody can exercise.
 *
 * **Three transforms over the built tree, and they compose in this order:** `sliceTo`
 * re-roots it (`KLQ-20260820-005`), `filterToPhase` prunes it (`KLQ-20260827-002`), and
 * `groupByObject` re-parents it (`KLQ-20260820-006`). All three are pure and run over data the
 * page already holds, which is what makes every one of those three controls cost a render
 * rather than a request. A fourth axis belongs beside `groupByObject`, not inside a component.
 *
 * **This module computes no visibility.** A scoped member receives an already-pruned tree and
 * an already-filtered slot list from the server (`hierarchy.scope.ScopeView`), and a client
 * that filtered again would be a second implementation of the zone rule — the one the retro
 * calls out as how "X cannot reach Y" ends up enforced in one place and forgotten in the
 * other. Everything here is arrangement: parenting, ordering, and attaching a version to the
 * drawing it belongs to.
 */

import type { SlicerOption } from "@/components";
import type { ProjectObject } from "@/features/projects";
import { objectBucketId, objectPivotGroups } from "@/features/projects";
import { bucketBy } from "@/lib/pivot";
import type { PivotBucket } from "@/lib/pivot";

import type {
  CurrentVersion,
  DeliverableStatus,
  HierarchyNode,
  OwningTask,
  Slot,
} from "./api";

/** A container level, with whatever the project put beneath it. */
export interface ContainerNode {
  readonly kind: "container";
  readonly id: string;
  readonly name: string;
  readonly nodeKind: string;
  readonly children: readonly TreeNode[];
  /**
   * Drawings anywhere beneath this node — what "(empty)" is decided from.
   *
   * **The server's number where there is one** (`KLQ-20260825-008` §5.1). Since the contents
   * are fetched a level at a time, counting the loaded children would report a floor nobody has
   * expanded as empty — which is a false statement, not a missing one. `GET /hierarchy` sends
   * `drawing_count` on every node with the caller's scope already applied, and that is what
   * this holds. A synthetic pivot heading has no server row, so it still counts its children.
   */
  readonly drawingCount: number;
  /**
   * True for a heading a pivot invented, false for a level the project actually has
   * (`KLQ-20260820-006` §0.1).
   *
   * **Required rather than optional, so every construction site has to answer it.** A
   * container means *a place* everywhere else in this app: it is what a rename PATCHes, what
   * a new drawing is filed under, what a duplicate copies. A pivot group is a heading over
   * rows that have not moved, and the database has no row for it — so a caller that treats
   * one as a place is filing a drawing under a node id that does not exist. The other two
   * halves of the same rule are the `object:` id prefix in `groupByObject` and the plain-text
   * rendering in `DrawingsTable`; this flag is what both of those read.
   */
  readonly synthetic: boolean;
  /**
   * Who is responsible for this level (`KLQ-20260828-003`), or `null` — which means *inherit*
   * and never *nobody*. Resolution is `responsible.ts`'s, not this module's: `buildTree` owns
   * where a row sits, and who owns it is a second question over the same shape.
   *
   * `null` on a synthetic heading always, and on a real node the caller may only *see*: the
   * server strips the field for a node outside a scoped member's grant, so a name never
   * reaches a client that could not reach the row.
   */
  readonly responsibleUserId: string | null;
}

/** A drawing, with its current version if it has one. */
export interface SlotNode {
  readonly kind: "slot";
  readonly id: string;
  readonly name: string;
  readonly identifier: string;
  readonly drawingNumber: string | null;
  readonly title: string | null;
  readonly current: CurrentVersion | null;
  /**
   * The statutory object (SO/PS) this drawing is filed under (`KLQ-20260817-002`), or `null`
   * for the visible unassigned bucket. Read only by `groupByObject`: like `phaseId` below it
   * is an axis and never an address, so nothing in `buildTree` consults it when deciding
   * where a row sits.
   */
  readonly objectId: string | null;
  /**
   * Which stage of the project this drawing is filed under (`KLQ-20260827-002`), or `null` for
   * the visible "Unassigned" bucket. An axis, never a place: the drawing's position in this
   * tree does not move when its phase does, which is the whole reason phase is a column on the
   * slot rather than a level above it.
   */
  readonly phaseId: string | null;
  /**
   * The task this drawing is a deliverable of (`KLQ-20260824-001`) — a link back to the work,
   * never part of the address. `parent_id` still says where the drawing sits, and the
   * identifier never reads this field.
   */
  readonly owningTaskId: string | null;
  /**
   * The same task, named — or `null` because the server would not name it for this reader:
   * the task sits outside their zones, or their firm was never admitted to it. The pair is a
   * tri-state and each state renders differently, which is why the id is kept rather than
   * collapsed into this field: *unowned* and *owned by work you cannot see* are different
   * facts about a drawing and a planner is entitled to both.
   */
  readonly owningTask: OwningTask | null;
  /** Derived server-side from the slot's version and its open anchored tasks; never stored. */
  readonly status: DeliverableStatus;
  /**
   * **Where this drawing sits, as the names on the way down to it** — root first, its own
   * parent last, empty for a drawing filed at the top of the project.
   *
   * Carried on the row rather than read off the tree, because the arrangements that need it
   * are exactly the ones that no longer have a tree to read: pivoted by object, the parent
   * above a drawing is `SO 108` and the floor it is on is nowhere on screen; flat, there is no
   * parent at all. The owner's ask on 2026-09-04 is *both at once* — grouped by object, still
   * able to see the location — and one field on the row is what makes that a column instead of
   * a second traversal per render.
   *
   * It is the **absolute** path, unaffected by a slice: re-rooting the view changes which rows
   * are drawn, never where they actually are in the project.
   */
  readonly location: readonly string[];
}

export type TreeNode = ContainerNode | SlotNode;

export interface DrawingsTree {
  readonly roots: readonly TreeNode[];
  /** Every drawing in the tree, flat — the slot picker's list and the counts' source. */
  readonly slots: readonly SlotNode[];
  readonly drawingCount: number;
}

export const EMPTY_TREE: DrawingsTree = {
  roots: [],
  slots: [],
  drawingCount: 0,
};

/**
 * Whether a project has a shape yet — any live level below its root (`KLQ-20261023-011`).
 *
 * A project is created with its root, so "has a live node" stopped meaning "has a shape" on
 * 2026-10-28: a tree holding only the root, or the root plus drawings filed straight onto it,
 * is still a flat project, and the setup gate offers it the wizard. A *level* is a live node
 * with a parent that is neither a drawing (`slot`) nor the unfiled bucket.
 */
export function hasLevels(nodes: readonly HierarchyNode[]): boolean {
  return nodes.some(
    (node) =>
      node.archived_at == null &&
      node.parent_id != null &&
      node.kind !== "slot" &&
      node.kind !== "unfiled",
  );
}

/**
 * How many drawings a flat project holds before the screen offers it a structure — owner
 * decision of 2026-10-28 (`KLQ-20261023-011`, Notes 1). Below it a flat list is simply a
 * short list; past it, sorting by building and floor starts to pay for itself.
 */
export const STRUCTURE_PROMPT_THRESHOLD = 10;

/**
 * Whether the drawings screen draws „Chceš výkresy roztriediť podľa budov a podlaží?“
 * (`KLQ-20261023-011` §3). The structure is offered, never imposed: only to a writer, only once
 * the tree has answered (`nodes` undefined is "not yet", never "flat"), only while the project
 * has no level below its root — every drawing then sits at the root — and only from the
 * threshold up. Pure and out here so the threshold is a tested number, not a JSX condition.
 */
export function offersStructure({
  canWrite,
  nodes,
  drawingCount,
}: {
  readonly canWrite: boolean;
  readonly nodes: readonly HierarchyNode[] | undefined;
  readonly drawingCount: number;
}): boolean {
  return (
    canWrite &&
    nodes !== undefined &&
    !hasLevels(nodes) &&
    drawingCount >= STRUCTURE_PROMPT_THRESHOLD
  );
}

/**
 * Build the render tree.
 *
 * The server returns nodes in `display_order` within a parent, and slots ordered by
 * identifier; both orders are preserved rather than re-sorted, because the tree's order is a
 * project decision (`PATCH /hierarchy` is what changes it) and re-sorting client-side would
 * make the screen disagree with the editor that sets it.
 *
 * A slot whose parent is missing from the node list cannot happen for an unscoped caller and
 * *can* for a scoped one whose subtree was pruned mid-request; it is dropped rather than
 * promoted to a root, because a drawing rendered at the top of somebody's tree is a drawing
 * they will file wrongly.
 */
export function buildTree(
  nodes: readonly HierarchyNode[],
  slots: readonly Slot[],
  currents: readonly CurrentVersion[],
): DrawingsTree {
  const currentBySlot = new Map(
    currents.map((version) => [version.slot_id, version]),
  );

  const slotNodes = new Map<string, SlotNode>();
  const slotParents = new Map<string, string | null>();
  for (const slot of slots) {
    slotNodes.set(slot.id, {
      kind: "slot",
      id: slot.id,
      name: slot.name,
      identifier: slot.slot_identifier,
      drawingNumber: slot.drawing_number ?? null,
      title: slot.title ?? null,
      current: currentBySlot.get(slot.id) ?? null,
      objectId: slot.object_id ?? null,
      phaseId: slot.phase_id ?? null,
      owningTaskId: slot.owning_task_id ?? null,
      owningTask: slot.owning_task ?? null,
      status: slot.status,
      // Filled in by `build`, which is the only place that knows the way down. A slot the
      // walk never reaches is dropped rather than rendered, so this default never ships.
      location: [],
    });
    slotParents.set(slot.id, slot.parent_id ?? null);
  }

  // Containers only. Since 2026-08-25 the hierarchy endpoint sends containers alone
  // (`hierarchy.service.read_tree`, `include_slots=False`) — the drawings arrive through
  // `GET /slots`, richer. The `slotNodes.has` guard stays because a client running against an
  // older server still has to drop them. Archived branches are not rendered: the server keeps
  // them for history, and a greyed-out tree is a different feature from a browsable one.
  const containers = nodes.filter(
    (node) =>
      node.kind !== "slot" &&
      !slotNodes.has(node.id) &&
      node.archived_at == null,
  );

  const childrenOf = new Map<string | null, HierarchyNode[]>();
  for (const node of containers) {
    const key = node.parent_id ?? null;
    const bucket = childrenOf.get(key);
    if (bucket === undefined) childrenOf.set(key, [node]);
    else bucket.push(node);
  }

  const slotsOf = new Map<string | null, SlotNode[]>();
  for (const [slotId, parentId] of slotParents) {
    const slot = slotNodes.get(slotId);
    if (slot === undefined) continue;
    const bucket = slotsOf.get(parentId);
    if (bucket === undefined) slotsOf.set(parentId, [slot]);
    else bucket.push(slot);
  }

  function build(
    parentId: string | null,
    trail: readonly string[],
  ): readonly TreeNode[] {
    const children: TreeNode[] = [];
    // **Drawings before sub-levels, and that ordering is the point** (owner, 2026-08-27:
    // *"výkres má byť hore a nie pod súradnicovými levels"*). A floor that holds four zones
    // and two drawings used to render both drawings below every zone and everything inside
    // them, so the sheets filed *on this floor* were the last thing on screen and the further
    // a project nested the further down they went. A drawing is what somebody came here to
    // open; a level is how they got here.
    //
    // Order *within* each of the two groups is still the server's — `display_order` for
    // containers, identifier for slots — and is deliberately not re-sorted here: the tree's
    // order is a project decision that `PATCH /hierarchy` owns.
    // The trail is the drawing's location, stamped on the copy that is rendered — see
    // `SlotNode.location`. Copied rather than mutated so that the map above stays the raw
    // reading of the response and every consumer gets the same object shape.
    for (const slot of slotsOf.get(parentId) ?? [])
      children.push({ ...slot, location: trail });
    for (const node of childrenOf.get(parentId) ?? []) {
      const nested = build(node.id, [...trail, node.name]);
      children.push({
        kind: "container",
        id: node.id,
        name: node.name,
        nodeKind: node.kind,
        synthetic: false,
        responsibleUserId: node.responsible_user_id ?? null,
        children: nested,
        // The shape's own answer, falling back to the children for a response that predates
        // the field. `?? ` and not `||`: a level with genuinely no drawings sends `0`, and
        // `||` would throw that away and count the loaded children instead — which is the
        // same number today and silently wrong the moment a branch is unfetched.
        drawingCount:
          node.drawing_count ??
          nested.reduce(
            (total, child) =>
              total + (child.kind === "slot" ? 1 : child.drawingCount),
            0,
          ),
      });
    }
    return children;
  }

  const roots = build(null, []);

  // The flat list is *what the tree renders*, not what the slot list contained: the picker in
  // the publish drawer offers from it, and offering a drawing the tree cannot show would let
  // somebody publish into a place they cannot then find it.
  const flat: SlotNode[] = [];
  const collect = (items: readonly TreeNode[]): void => {
    for (const item of items) {
      if (item.kind === "slot") flat.push(item);
      else collect(item.children);
    }
  };
  collect(roots);

  return { roots, slots: flat, drawingCount: countOf(roots) };
}

/**
 * How many drawings a set of rows stands for — **the shape's count, not the loaded rows'**.
 *
 * `flat.length` was the answer while the page held every slot in the project. It stopped being
 * one on 2026-09-10: with the contents fetched a level at a time, the number of loaded slots is
 * a fact about what somebody has expanded, and the two places that read it — *"your slice hides
 * 340"* and the *"this project has no drawings yet"* panel — are both asking about the project.
 *
 * A slot counts as itself; a container answers for its whole subtree, so its children are not
 * walked again.
 */
function countOf(nodes: readonly TreeNode[]): number {
  return nodes.reduce(
    (total, node) => total + (node.kind === "slot" ? 1 : node.drawingCount),
    0,
  );
}

/** Every container id in the tree — what "expand all" and the initial expansion work from. */
export function containerIds(nodes: readonly TreeNode[]): string[] {
  const ids: string[] = [];
  const walk = (items: readonly TreeNode[]): void => {
    for (const item of items) {
      if (item.kind === "container") {
        ids.push(item.id);
        walk(item.children);
      }
    }
  };
  walk(nodes);
  return ids;
}

/** The container ids on the path down to a node, so selecting a drawing can reveal it. */
export function pathTo(nodes: readonly TreeNode[], targetId: string): string[] {
  const found: string[] = [];
  const walk = (items: readonly TreeNode[], trail: string[]): boolean => {
    for (const item of items) {
      if (item.id === targetId) {
        found.push(...trail);
        return true;
      }
      if (item.kind === "container" && walk(item.children, [...trail, item.id]))
        return true;
    }
    return false;
  };
  walk(nodes, []);
  return found;
}

/**
 * Re-root the render tree at the sliced nodes (`KLQ-20260820-005`).
 *
 * **Client-side, and only for the tree.** The tree is one fetch per project session and is
 * already in memory, capped by the generator at a size a dictionary walk does not notice — so
 * changing the slice costs no request. Everything paginated (the feed, and the register after
 * it) slices on the server instead, because those are the collections that run to thousands of
 * rows and cannot be filtered a page at a time.
 *
 * **A slice is not a scope.** Nothing here decides what the person may see: the server already
 * pruned the tree to their zones (`hierarchy.scope`), and this only chooses which part of what
 * they may see is on screen. An id that is not in the tree contributes nothing rather than
 * throwing, because a slice can outlive the branch it names — somebody else may have removed it
 * between the URL being shared and the page being opened.
 */
export function sliceTo(
  tree: DrawingsTree,
  under: readonly string[],
): DrawingsTree {
  if (under.length === 0) return tree;

  const wanted = new Set(under);
  const picked: TreeNode[] = [];
  const walk = (items: readonly TreeNode[]): void => {
    for (const item of items) {
      if (wanted.has(item.id)) {
        picked.push(item);
        continue; // Its own subtree comes with it; a nested selection would double it.
      }
      if (item.kind === "container") walk(item.children);
    }
  };
  walk(tree.roots);

  const flat: SlotNode[] = [];
  const collect = (items: readonly TreeNode[]): void => {
    for (const item of items) {
      if (item.kind === "slot") flat.push(item);
      else collect(item.children);
    }
  };
  collect(picked);

  // The slice's own subtree counts, summed — which is exactly *"how many drawings this slice
  // is about"*, and stays right whether or not anybody has expanded a row inside it.
  return { roots: picked, slots: flat, drawingCount: countOf(picked) };
}

/**
 * The tree with only the drawings of one phase left standing, and the containers that lead to
 * them (`KLQ-20260827-002`).
 *
 * **A filter, not a scope, and not a fetch.** The same two disclaimers `sliceTo` carries: the
 * server has already pruned this tree to the reader's zones, and the page holds the whole slot
 * list, so switching phase costs a render and no request. A container that ends up holding
 * nothing is dropped rather than rendered empty — an empty branch reads as "nothing was
 * published here", which is a different and false statement from "nothing here is in this
 * phase".
 *
 * `phaseId === null` is the caller asking for *All*, which is the absence of a filter rather
 * than a phase of its own — a drawing filed under no phase is only reachable that way, and
 * that is deliberate: the "Unassigned" bucket is visible in the register, not a chip.
 */
/**
 * A drawing's location as one cell of text — what the clipboard copies (`KLQ-20260820-006`).
 *
 * **Extracted from `DrawingsTable`'s column so it can be tested**, the same move `boardColumns`
 * needed: while it was an inline `value:` on a column object, the only way to check it was to
 * mount a table. The criterion is *"the path copies as text, so a register pasted into a sheet
 * carries the location column with it"*, and the copying half is already proven by
 * `lib/grid-interop/clipboard.test.ts` — this is the half that says what the cell contains.
 *
 * A container prints nothing. In flat mode there are none; in an object pivot the only
 * containers are the synthetic `SO` headings, and repeating a location on a heading that groups
 * several drawings would be a claim about the group that is not true.
 *
 * The separator is ` / ` and the path is **absolute**, so a sliced view still says which block a
 * drawing is in rather than which block the slice starts at.
 */
export function locationText(row: TreeNode): string {
  return row.kind === "slot" ? row.location.join(" / ") : "";
}


export function filterToPhase(
  tree: DrawingsTree,
  phaseId: string | null,
): DrawingsTree {
  if (phaseId === null) return tree;

  const keep = (items: readonly TreeNode[]): TreeNode[] => {
    const out: TreeNode[] = [];
    for (const item of items) {
      if (item.kind === "slot") {
        if (item.phaseId === phaseId) out.push(item);
        continue;
      }
      const children = keep(item.children);
      if (children.length > 0) out.push({ ...item, children });
    }
    return out;
  };

  const roots = keep(tree.roots);
  const flat: SlotNode[] = [];
  const collect = (items: readonly TreeNode[]): void => {
    for (const item of items) {
      if (item.kind === "slot") flat.push(item);
      else collect(item.children);
    }
  };
  collect(roots);

  return { roots, slots: flat, drawingCount: flat.length };
}


/**
 * The tree regrouped under the object breakdown instead of the location tree
 * (`KLQ-20260820-006` §0).
 *
 * The third of this module's three transforms, and the only one that **re-parents**:
 * `sliceTo` re-roots, `filterToPhase` prunes, this one keeps every row and replaces the
 * headings above them. Pure and render-time, like both of the others — the page holds every
 * slot of the project unpaginated, so switching axis costs a `useMemo` and no request, which
 * is what `MCK-20260812-011` requires of register grouping in the first place.
 *
 * **Nothing here moves anything.** A pivot is a read-time arrangement; `parent_id` in the
 * database is untouched, and the same drawing is in the same place the moment the axis is
 * switched back. The groups it invents say so three ways — `synthetic: true`, an
 * `object:`-prefixed id that can never be mistaken for a hierarchy node id, and the plain-text
 * rendering `DrawingsTable` gives them. Consumers that want *places* (the parent pickers) must
 * read the pre-pivot tree.
 *
 * **Every drawing survives exactly once.** Groups nest the way objects do, an object with
 * nothing beneath it anywhere is not drawn, and everything left over lands in one visible
 * bucket at the end — a drawing with no object, and equally a drawing filed under an object
 * that has since been archived and is therefore absent from `objects`. Silently dropping
 * either would make the page's `hiddenDrawings` count blame the slice for rows the pivot ate.
 *
 * The bucket's wording arrives as an argument for the reason `Slicer` and `TreeTable` take
 * theirs that way: this module holds no copy.
 */
export function groupByObject(
  tree: DrawingsTree,
  objects: readonly ProjectObject[],
  unassignedLabel: string,
): DrawingsTree {
  // The grouping rule itself is `lib/pivot.ts` (`KLQ-20260904-004` §A) — which slot lands in
  // which bucket, how buckets nest, what happens to a slot that answers none. What stays here
  // is the only part that is this feature's: turning a bucket into a `TreeNode`.
  const buckets = bucketBy(tree.slots, {
    // The bucket list is the composition's, built once for every surface that groups by object
    // (`features/projects/objects.ts`) rather than three times.
    groups: objectPivotGroups(objects),
    // At most one, always: a drawing carries one `object_id`. A slot filed under an object
    // that has since been archived answers a key this spec does not have, which `bucketBy`
    // treats as no key at all — the leftover bucket, exactly as before.
    keysOf: (slot) => (slot.objectId === null ? [] : [objectBucketId(slot.objectId)]),
    unassignedLabel,
  });

  const toNode = (bucket: PivotBucket<SlotNode>): TreeNode => ({
    kind: "container",
    id: bucket.id,
    name: bucket.label,
    nodeKind: "object",
    synthetic: true,
    // A heading a pivot invented has no row in the database, so it carries no value of its own
    // and passes its ancestors' straight through (`KLQ-20260828-003`).
    responsibleUserId: null,
    // Drawings before sub-groups, the same order `build` uses for the location tree — a sheet
    // filed on this object is what somebody came here to open, a sub-object is how they would
    // get further down.
    children: [...bucket.leaves, ...bucket.children.map(toNode)],
    drawingCount: bucket.count,
  });

  // Deliberately `tree.slots` and not a re-walk: the pivot is a total rearrangement of exactly
  // those rows, so recomputing the flat list would only be a chance to disagree with itself.
  return {
    roots: buckets.map(toNode),
    slots: tree.slots,
    drawingCount: tree.slots.length,
  };
}

/**
 * Every container in the tree, as slicer options — the *Location* facet, on every screen that
 * has a rail (`KLQ-20260825-002` §1).
 *
 * It lives here rather than beside any one of them because the hierarchy is this feature's
 * data: drawings, tasks and reviews all slice by the same tree, and three derivations of one
 * option list is how two of them end up offering a level the third does not (retro rule 8).
 *
 * Containers only, and no slots. A drawing is a leaf of the thing being sliced, not a place to
 * slice to, and a picker that offered every slot would be the tree screen `KLQ-20260821-001`
 * §A declined to build a second time.
 *
 * Not to be confused with `StructureDrawers`' `containerOptions`, which flattens the *render*
 * tree for a `<select>` of parents to file something under. Different input, different shape,
 * different question.
 */
export function slicerOptions(
  nodes: readonly HierarchyNode[],
): readonly SlicerOption[] {
  // No slots and no current versions: this offers containers, and asking the other two
  // queries to throw their answers away would be two requests for nothing.
  const options = (items: readonly TreeNode[]): readonly SlicerOption[] =>
    items
      .filter((item) => item.kind === "container")
      .map((item) => ({
        id: item.id,
        label: item.name,
        children: options(item.children),
      }));
  return options(buildTree(nodes, [], []).roots);
}
